<script setup lang="ts">
import {ToggleSwitch, Icon} from "@/Components/Library";
import User from "@/Models/User";
import {computed, watchEffect} from "vue";
import {useStorage} from "@vueuse/core/index";
import {useRouter} from "vue-router";
import {useMainStore} from "../Stores/store";
import useAuth from "@/Composables/auth";

const router = useRouter();
const $store = useMainStore();
const $user = new User();
const {logOut} = useAuth();
const darkMode = useStorage(
    "color-scheme",
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
);

const emit = defineEmits<{
    showAskAlbieDrawer: [];
    close: [];
}>();

const isPrivileged = computed(() => $user.hasAnyRole(["admin", "super-admin"]) || !!$store.user.is_group_admin);

const userMenu = computed(() => [
    {
        label: "Groups",
        icon: {icon: "prime:users", width: 20},
        to: "feed",
        count: $store.user.pending_group_invitations_count,
    },
    // ...(isPrivileged.value
    //     ? [
    //           {
    //               label: "Groups",
    //               icon: {icon: "prime:users", width: 20},
    //               to: "groups.index",
    //               count: $store.user.pending_group_invitations_count,
    //               permissions: ["reuse questions"],
    //           },
    //       ]
    //     : []),
    {
        label: "Contacts",
        icon: {icon: "prime:address-book", width: 20},
        to: "contacts.index",
        count: $store.user.pending_contact_invitations_count,
    },
    {
        label: "Chats",
        icon: {icon: "prime:comment", width: 20},
        to: "conversations.index",
        count: $store.user.unread_conversations_count,
    },
    {
        label: "Posts",
        icon: {icon: "mage:message-question-mark-round", width: 18},
        to: "questions.index",
        count: 0,
        permissions: ["reuse questions"],
    },
    {
        label: "Responses",
        icon: {icon: "gg:check-r"},
        to: "responses.index",
        count: 0,
        permissions: ["reuse questions"],
    },
    {
        label: "Stimuli",
        icon: {icon: "prime:image", width: 20},
        to: "stimuli.index",
        count: 0,
        permissions: ["reuse questions"],
    },
    // {
    //     label: "Profile",
    //     icon: "prime:user",
    //     to: "profile.edit",
    // },
    // {
    //     label: "Log out",
    //     icon: "prime:sign-out",
    //     command: () => {
    //         logOut();
    //     },
    // },
]);
watchEffect(() => {
    if (darkMode.value === "dark") {
        document.documentElement.classList.add("dark");
    } else {
        document.documentElement.classList.remove("dark");
    }
});
</script>
<template>
    <div class="flex h-full grow flex-col">
        <div v-if="$user.hasAnyRole(['admin', 'super-admin'])">
            <router-link
                @click="emit('close')"
                :to="{name: 'admin.dashboard'}"
                class="mb-2 flex items-center gap-3 rounded-md px-2 py-2 text-amber-700 transition hover:bg-stone-50 dark:text-amber-400 dark:hover:bg-stone-950"
            >
                <Icon width="20" icon="prime:cog" />
                Admin
            </router-link>
            <hr class="mb-2 dark:border-stone-700" />
        </div>
        <template v-for="(item, i) in userMenu" :key="i">
            <template v-if="!item.permissions || !item.permissions.length || $user.can(item.permissions)">
                <template v-if="item.to">
                    <router-link
                        @click="emit('close')"
                        :to="{name: item.to}"
                        class="flex items-center gap-3 rounded-md px-2 py-2 transition hover:bg-stone-50 dark:hover:bg-stone-800"
                        :class="{
                            'bg-stone-100 dark:bg-stone-950': $route.name === item.to,
                        }"
                    >
                        <span class="flex w-5 justify-center">
                            <Icon v-bind="item.icon" />
                        </span>
                        <span>
                            {{ item.label }}
                        </span>
                        <div
                            v-if="item.count && item.count > 0"
                            class="ml-auto min-w-5 rounded-full bg-red-600 px-2 py-0.5 text-center text-xs font-medium text-white dark:bg-red-800"
                        >
                            {{ item.count }}
                        </div>
                    </router-link>
                </template>
                <template v-else>
                    <button
                        @click="item.command"
                        class="flex w-full items-center gap-3 px-2 py-2 transition hover:bg-stone-50 dark:hover:bg-stone-950"
                    >
                        <span class="flex w-5 justify-center">
                            <Icon v-bind="item.icon" />
                        </span>
                        {{ item.label }}
                    </button>
                </template>
            </template>
        </template>
        <div class="mt-auto">
            <div class="justify--center mb-3 flex w-full items-center gap-2 px-2">
                <Icon width="26" icon="prime:sun" />
                <ToggleSwitch v-model="darkMode" trueValue="dark" falseValue="light" />
                <Icon width="26" icon="prime:moon" />
            </div>
            <router-link
                @click="emit('close')"
                :to="{name: 'profile.edit'}"
                class="flex items-center gap-3 rounded-md px-2 py-2 transition hover:bg-stone-50 dark:hover:bg-stone-800"
                :class="{
                    'bg-stone-50 bg-stone-950': $route.name === 'profile.edit',
                }"
            >
                <Icon width="20" icon="prime:user" />
                Account
            </router-link>
            <button
                @click="logOut"
                class="flex w-full items-center gap-3 rounded-md px-2 py-2 transition hover:bg-stone-50 dark:hover:bg-stone-950"
            >
                <Icon width="20" icon="prime:sign-out" />
                Log out
            </button>
        </div>
        <div class="px--4 mt-4 flex items-center">
            <router-link @click="emit('close')" :to="{name: 'terms'}" class="text-xs text-stone-500">
                Terms and Privacy Policy
            </router-link>
            <button class="ml-auto text-stone-500" @click="emit('showAskAlbieDrawer')">
                <Icon icon="ri:robot-2-fill" width="22" />
            </button>
        </div>
    </div>
</template>
<style scoped></style>
